import axios from 'axios';
import { normalizeList, normalizeItem, normalizeError, normalizePagination, normalizeCount } from './apiNormalizer';
import { emitIntelligenceRefresh } from './intelligenceRefresh';
import { detectCountryHint } from './razorpay';

// Re-export normalizer functions for convenience
export { normalizeList, normalizeItem, normalizeError, normalizePagination, normalizeCount } from './apiNormalizer';

// Read API URL from environment variable for production deployment
// Falls back to window.location.origin for same-origin requests
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

if (process.env.NODE_ENV !== 'production') {
   
  console.log('[API] Using base URL:', API_BASE_URL);
}

// Iter 62.4 Bug 2.6: per-endpoint timeout helper.
// Brain / LLM / Apollo endpoints can legitimately take >30s; using a single
// global 30s timeout caused premature client-side aborts. Endpoints not in
// this list use the default `timeout` set on the axios instance below.
const LONG_RUNNING_PREFIXES = [
  '/api/brain/',
  '/api/orchestrator/',
  '/api/ai/cadence',
  '/api/ai/insights',
  '/api/ai/sales',
  '/api/leads/discover',
  '/api/leads/enrich',
  '/api/leads/bulk-import',
  '/api/admin/onboarding',
  '/api/data-import/',
];
const LONG_RUNNING_TIMEOUT_MS = 120000; // 2 minutes

// Auth endpoints get a longer timeout so a one-time backend COLD START (first
// request after an idle pod can take 30-60s) doesn't abort at 30s and surface a
// misleading "invalid credentials" error.
const AUTH_PREFIXES = ['/api/auth/login', '/api/auth/register', '/api/auth/refresh'];
const AUTH_TIMEOUT_MS = 90000; // 90s

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // default 30s
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Apply the longer timeout to brain/LLM/Apollo endpoints that legitimately
    // take >30s, so they don't abort with "timeout of 30000ms exceeded".
    if (config.timeout === undefined || config.timeout === 30000) {
      const url = config.url || '';
      if (LONG_RUNNING_PREFIXES.some((p) => url.includes(p))) {
        config.timeout = LONG_RUNNING_TIMEOUT_MS;
      } else if (AUTH_PREFIXES.some((p) => url.includes(p))) {
        config.timeout = AUTH_TIMEOUT_MS;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and normalize responses
api.interceptors.response.use(
  (response) => {
    // Ensure response.data is never undefined
    if (response.data === undefined) {
      response.data = null;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Automatic retry for 5xx / network / timeout errors — IDEMPOTENT methods only.
    // Retrying a POST/PUT/PATCH that timed out could duplicate a write (e.g. a note
    // that actually saved server-side), so we only auto-retry GET/HEAD requests.
    const method = (originalRequest?.method || 'get').toLowerCase();
    const isIdempotent = method === 'get' || method === 'head';
    const isRetryable = isIdempotent && (!error.response || error.response.status >= 500);
    if (isRetryable && originalRequest && !originalRequest.url?.includes('/auth/')) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      if (originalRequest._retryCount <= 2) {
        const delay = originalRequest._retryCount * 1500; // 1.5s, 3s
        await new Promise(r => setTimeout(r, delay));
        return api(originalRequest);
      }
    }

    // If 401 and not already retrying, check for session expiration
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if session expired header is present
      const sessionExpired = error.response?.headers?.['x-session-expired'] === 'true' ||
                            error.response?.data?.detail === 'Session expired';
      
      if (sessionExpired) {
        // Session expired - redirect to login silently
        console.log('[API] Session expired, redirecting to login');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        // Redirect without the scary expired toast — user just needs to log in again
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(
            `${API_BASE_URL}/api/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const { access_token, refresh_token } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Normalize object `detail` from backend (e.g. plan_gate returns {message, current_plan, ...})
    // Convert to string so toast.error() and React rendering never receive a raw object
    if (error.response?.data?.detail && typeof error.response.data.detail === 'object' && !Array.isArray(error.response.data.detail)) {
      error.response.data.detail = error.response.data.detail.message || error.response.data.detail.msg || JSON.stringify(error.response.data.detail);
    }

    // Detect client-side timeouts so the UI shows a friendly message instead of
    // the raw "timeout of 30000ms exceeded".
    const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');

    // Create structured error object
    const structuredError = {
      status: error.response?.status || 0,
      message: isTimeout
        ? 'The server is taking longer than usual. Please try again in a moment.'
        : normalizeError(error, 'Request failed'),
      detail: error.response?.data?.detail || null,
      raw: error.response?.data || null,
      isNetworkError: !error.response,
      isTimeout,
      isAuthError: error.response?.status === 401 || error.response?.status === 403,
      isRateLimited: error.response?.status === 429,
      isServerError: error.response?.status >= 500,
    };

    // Attach structured error to the error object
    error.structured = structuredError;

    // Auto-toast for rate limiting and server errors (non-auth pages)
    if (error.response?.status === 429) {
      const url = error.config?.url || '';
      // Iter 37: silence 429s for background polls (credits, vendor-health, etc.) —
      // these fire on every nav and would otherwise spam the user with red toasts.
      const SILENT_429_PATHS = ['/credits/summary', '/credits/balance', '/vendor-health', '/stuck-jobs'];
      const isSilent = SILENT_429_PATHS.some(p => url.includes(p));
      if (!isSilent) {
        const retryAfter = error.response.headers?.['retry-after'];
        const msg = retryAfter
          ? `Too many requests. Please wait ${retryAfter}s.`
          : 'Too many requests. Please slow down.';
        if (typeof window !== 'undefined' && window.__sonnerToast) {
          window.__sonnerToast(msg);
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Helper to safely get list data from API response
 * @param {Promise} apiCall - The API promise
 * @param {string} listKey - Optional key to extract list from
 * @returns {Promise<Array>} - Always returns an array
 */
export async function fetchList(apiCall, listKey = null) {
  try {
    const response = await apiCall;
    return normalizeList(response?.data, listKey);
  } catch (error) {
    console.error('[API] fetchList error:', error.structured?.message || error.message);
    return [];
  }
}

/**
 * Helper to safely get single item from API response
 * @param {Promise} apiCall - The API promise
 * @param {string} itemKey - Optional key to extract item from
 * @returns {Promise<Object|null>} - Returns the item or null
 */
export async function fetchItem(apiCall, itemKey = null) {
  try {
    const response = await apiCall;
    return normalizeItem(response?.data, itemKey) || response?.data || null;
  } catch (error) {
    console.error('[API] fetchItem error:', error.structured?.message || error.message);
    return null;
  }
}

/**
 * Helper to safely get count from API response
 * @param {Promise} apiCall - The API promise
 * @returns {Promise<number>} - Always returns a number
 */
export async function fetchCount(apiCall) {
  try {
    const response = await apiCall;
    return normalizeCount(response?.data);
  } catch (error) {
    console.error('[API] fetchCount error:', error.structured?.message || error.message);
    return 0;
  }
}


export const helpAPI = {
  track: (guideId) => api.post('/api/help/track', { guide_id: guideId }),
  feedback: (guideId, helpful, comment) => api.post('/api/help/feedback', { guide_id: guideId, helpful, comment }),
  analytics: () => api.get('/api/help/analytics'),
};

export default api;

// Auth API
export const authAPI = {
  signup: (data) => api.post('/api/auth/signup', data, { headers: { 'x-country-hint': detectCountryHint() || '' } }),
  login: (data) => api.post('/api/auth/login', data),
  checkEmail: (email) => api.post('/api/auth/check-email', { email }),
  getCurrentUser: () => api.get('/api/users/me'),
  logout: () => api.post('/api/auth/session/logout'),
  refreshSession: () => api.post('/api/auth/session/refresh'),
  getSessionInfo: () => api.get('/api/auth/session/info'),
  // Google OAuth - Direct integration
  // Legacy id-token flow (kept for backward compat).
  googleVerify: (data) => api.post('/api/auth/google/verify', data),
  // Auth-code flow (popup) — lets us force `prompt=select_account` so the
  // Google account chooser always appears instead of silently pre-selecting
  // the browser's active Google session.
  googleCode: (data) => api.post('/api/auth/google/code', data),
  getGoogleClientId: () => api.get('/api/auth/google/client-id'),
};

// Brain Health / Activation readout — with a shared 30s cache so the card and
// banner share ONE request and, crucially, a resolved result survives the
// parent's rapid re-mounts (a fresh mount reads the cache synchronously instead
// of restarting a fetch that gets dropped mid-flight → the old "stuck loading").
let _brainHealthCache = { data: null, ts: 0, promise: null };
export const brainHealthAPI = {
  get: () => api.get('/api/brain/health'),
  peek: () => (_brainHealthCache.data && Date.now() - _brainHealthCache.ts < 30000 ? _brainHealthCache.data : null),
  invalidate: () => { _brainHealthCache = { data: null, ts: 0, promise: null }; },
  runEngineNow: () => api.post('/api/brain/run-engine-now'),
  getCached: () => {
    const fresh = _brainHealthCache.data && Date.now() - _brainHealthCache.ts < 30000;
    if (fresh) return Promise.resolve(_brainHealthCache.data);
    if (!_brainHealthCache.promise) {
      _brainHealthCache.promise = api.get('/api/brain/health')
        .then((r) => {
          const d = r.data?.data || r.data;
          _brainHealthCache = { data: d, ts: Date.now(), promise: null };
          return d;
        })
        .catch((e) => { _brainHealthCache.promise = null; throw e; });
    }
    return _brainHealthCache.promise;
  },
};

// Live-readiness (Go-LIVE dialog) — shared cached promise so a Radix portal
// remount can't abort the in-flight XHR and leave the dialog stuck on
// "Checking readiness…". A resolved result survives rapid re-mounts (a fresh
// mount reads the cache/in-flight promise instead of starting a request that
// gets dropped mid-flight). Mirrors brainHealthAPI.getCached.
let _liveReadinessCache = { data: null, ts: 0, promise: null };
export const liveReadinessAPI = {
  invalidate: () => { _liveReadinessCache = { data: null, ts: 0, promise: null }; },
  getCached: () => {
    const fresh = _liveReadinessCache.data && Date.now() - _liveReadinessCache.ts < 20000;
    if (fresh) return Promise.resolve(_liveReadinessCache.data);
    if (!_liveReadinessCache.promise) {
      _liveReadinessCache.promise = api.get('/api/tenant/ai/live-readiness')
        .then((r) => {
          const d = r.data?.data || r.data;
          _liveReadinessCache = { data: d, ts: Date.now(), promise: null };
          return d;
        })
        .catch((e) => { _liveReadinessCache.promise = null; throw e; });
    }
    return _liveReadinessCache.promise;
  },
};


// Leads API - with Intelligence Refresh
export const leadsAPI = {
  getAll: (params) => api.get('/api/leads/', { params }),
  exportCsv: (params) => api.get('/api/leads/export', { params, responseType: 'blob' }),
  getCount: (params) => api.get('/api/leads/count', { params }),
  getBreakdown: () => api.get('/api/leads/stats/breakdown'),
  getKanban: (params) => api.get('/api/leads/kanban', { params }),
  getBootstrap: () => api.get('/api/leads/_bootstrap'),
  getKanbanColumn: (stage, params) => api.get(`/api/leads/kanban/${encodeURIComponent(stage)}`, { params }),
  getAudience: (params) => api.get('/api/leads/audience', { params }),
  getNotRelevant: (params) => api.get('/api/leads/not-relevant', { params }),
  restoreRelevant: (id) => api.post(`/api/leads/${id}/restore-relevant`),
  bulkClearNotRelevant: (leadIds, all = false) => api.post('/api/leads/not-relevant/bulk-clear', { lead_ids: leadIds, all }),
  renameBatch: (old_name, new_name) => api.post('/api/leads/audience/batch/rename', { old_name, new_name }),
  deleteBatch: (name) => api.post('/api/leads/audience/batch/delete', { name }),
  promoteToPipeline: (id) => api.post(`/api/leads/${id}/promote-to-pipeline`).then(res => {
    emitIntelligenceRefresh('leads', 'update', id);
    return res;
  }),
  getOne: (id) => api.get(`/api/leads/${id}`),
  create: (data) => api.post('/api/leads/', data).then(res => {
    emitIntelligenceRefresh('leads', 'create', res.data?.id);
    return res;
  }),
  update: (id, data) => api.put(`/api/leads/${id}`, data).then(res => {
    emitIntelligenceRefresh('leads', 'update', id);
    return res;
  }),
  delete: (id) => api.delete(`/api/leads/${id}`).then(res => {
    emitIntelligenceRefresh('leads', 'delete', id);
    return res;
  }),
  bulkImport: (data) => api.post('/api/leads/bulk-import', data).then(res => {
    emitIntelligenceRefresh('leads', 'create', null);
    return res;
  }),
  bulkImportCheck: (data) => api.post('/api/leads/bulk-import/check', data),
  bulkDelete: (payload) => api.post('/api/leads/bulk-delete', Array.isArray(payload) ? { lead_ids: payload } : payload).then(res => {
    emitIntelligenceRefresh('leads', 'delete', null);
    return res.data;
  }),
  bulkMoveToAudience: (payload) => api.post('/api/leads/bulk-move-audience', Array.isArray(payload) ? { lead_ids: payload } : payload).then(res => {
    emitIntelligenceRefresh('leads', 'update', null);
    return res.data;
  }),
  bulkAssign: (payload) => api.post('/api/leads/bulk-assign', payload).then(res => {
    emitIntelligenceRefresh('leads', 'update', null);
    return res.data;
  }),
  bulkMoveToAudienceUndo: (leadIds) => api.post('/api/leads/bulk-move-audience/undo', { lead_ids: leadIds }).then(res => {
    emitIntelligenceRefresh('leads', 'update', null);
    return res.data;
  }),
  pipelineBatches: () => api.get('/api/leads/pipeline/batches'),
  selectBatch: (field, value) => api.post('/api/leads/select-batch', { field, value }),
  enrollmentHistory: (days = 7) => api.get(`/api/leads/enrollment-history?days=${days}`),
  enrollmentDayLeads: (date) => api.get(`/api/leads/enrollment-history/${date}/leads`),
  // CRM → AI Bridge endpoints
  getAIStatus: (leadId) => api.get(`/api/crm/leads/${leadId}/ai-status`),
  runSalesAI: (leadId, options = {}) => api.post(`/api/crm/leads/${leadId}/run-sales-ai`, options),
  getAIActivities: (leadId) => api.get(`/api/crm/leads/${leadId}/ai-activities`),
  initiateAICall: (leadId) => api.post(`/api/leads/${leadId}/ai-call`),
  getAICallStatus: (leadId, callId) => api.get(`/api/leads/${leadId}/ai-call/${callId}/status`),
  completeAICall: (leadId) => api.put(`/api/leads/${leadId}/ai-call-complete`),
  storeAICallSummary: (leadId, data) => api.post(`/api/leads/${leadId}/ai-call-summary`, data),
  getAICallSummaries: (leadId) => api.get(`/api/leads/${leadId}/ai-call-summaries`),
  // Phase 2 (b) — launch drafted commercial/closing cadence
  launchCommercialCadence: (leadId) => api.post(`/api/leads/${leadId}/launch-commercial-cadence`),
  // Intelligence
  analyzeIntelligence: (leadId) => api.post(`/api/leads/${leadId}/intelligence`),
  getLatestIntelligence: (leadId) => api.get(`/api/leads/${leadId}/intelligence/latest`),
  getIntelligenceTimeline: (leadId) => api.get(`/api/leads/${leadId}/intelligence/timeline`),
  hasNewActivity: (leadId) => api.get(`/api/leads/${leadId}/intelligence/has-new-activity`),
  draftEmail: (leadId, data) => api.post(`/api/leads/${leadId}/intelligence/draft-email`, data),
  // Engagement Score
  getEngagementScore: (leadId) => api.get(`/api/leads/engagement/score/${leadId}`),
  computeAllEngagementScores: () => api.post('/api/leads/engagement/scores/compute'),
  // Apollo Reveal
  revealContact: (leadId) => api.post(`/api/leads/${leadId}/reveal`),
  getRevealCredits: () => api.get('/api/leads/reveal/credits'),
  bulkReveal: (leadIds) => api.post('/api/leads/reveal/bulk', leadIds),
  // Discover Leads
  discoverLeads: () => api.post('/api/leads/discover'),
  importDiscoveredLeads: (leads) => api.post('/api/leads/discover/import', leads),
  // Reveal Analytics
  getRevealAnalytics: (days = 30) => api.get('/api/reveals/analytics', { params: { days } }),
  getResetStatus: () => api.get('/api/reveals/reset-status'),
  triggerReset: () => api.post('/api/reveals/trigger-reset'),
  bulkRevealColumn: (columnName) => api.post('/api/reveals/bulk-column', null, { params: { column_name: columnName } }),
};

// Lead Settings API
export const leadSettingsAPI = {
  getStatuses: () => api.get('/api/lead-settings/statuses'),
  createStatus: (data) => api.post('/api/lead-settings/statuses', data),
  updateStatus: (id, data) => api.put(`/api/lead-settings/statuses/${id}`, data),
  deleteStatus: (id) => api.delete(`/api/lead-settings/statuses/${id}`),
  getSources: () => api.get('/api/lead-settings/sources'),
  createSource: (data) => api.post('/api/lead-settings/sources', data),
  updateSource: (id, data) => api.put(`/api/lead-settings/sources/${id}`, data),
  deleteSource: (id) => api.delete(`/api/lead-settings/sources/${id}`),
  // Pipeline stages
  getStages: () => api.get('/api/lead-settings/stages'),
  createStage: (data) => api.post('/api/lead-settings/stages', data),
  updateStage: (id, data) => api.put(`/api/lead-settings/stages/${id}`, data),
  deleteStage: (id) => api.delete(`/api/lead-settings/stages/${id}`),
  reorderStages: (stageIds) => api.put('/api/lead-settings/stages/reorder', { stage_ids: stageIds }),
  setStageVisibility: (id, visible) => api.put(`/api/lead-settings/stages/${id}/visibility`, { visible }),
};

// Deal Stages API (tenant-configurable custom deal stages)
export const dealStagesAPI = {
  getStages: () => api.get('/api/deal-stages/'),
  createStage: (data) => api.post('/api/deal-stages/', data),
  deleteStage: (id) => api.delete(`/api/deal-stages/${id}`),
};

// Lead Activities API
export const leadActivitiesAPI = {
  getAll: (leadId, params) => api.get(`/api/leads/${leadId}/activities`, { params }),
  create: (leadId, data) => api.post(`/api/leads/${leadId}/activities`, data),
  // Notes
  getNotes: (leadId) => api.get(`/api/lead-activities/notes/${leadId}`),
  createNote: (data) => api.post('/api/lead-activities/notes', data),
  deleteNote: (noteId) => api.delete(`/api/lead-activities/notes/${noteId}`),
  getNoteComments: (noteId) => api.get(`/api/lead-activities/notes/${noteId}/comments`),
  addNoteComment: (noteId, text) => api.post(`/api/lead-activities/notes/${noteId}/comments`, { text }),
  // Tasks
  getTasks: (leadId) => api.get(`/api/lead-activities/tasks/${leadId}`),
  createTask: (data) => api.post('/api/lead-activities/tasks', data),
  toggleTask: (taskId, completed) => api.patch(`/api/lead-activities/tasks/${taskId}`, { completed }),
  updateTaskStatus: (taskId, status) => api.patch(`/api/lead-activities/tasks/${taskId}/status`, { status }),
  deleteTask: (taskId) => api.delete(`/api/lead-activities/tasks/${taskId}`),
  // Follow-ups
  getFollowUps: (leadId) => api.get(`/api/lead-activities/followups/${leadId}`),
  createFollowUp: (data) => api.post('/api/lead-activities/followups', data),
  updateFollowUpStatus: (followupId, status, summary) => api.patch(`/api/lead-activities/followups/${followupId}/status`, { status, summary }),
  rescheduleFollowUp: (followupId, newDate, notes) => api.patch(`/api/lead-activities/followups/${followupId}/reschedule`, { new_date: newDate, notes }),
  deleteFollowUp: (followupId) => api.delete(`/api/lead-activities/followups/${followupId}`),
  // Emails
  getEmails: (leadId) => api.get(`/api/lead-activities/emails/${leadId}`),
  createEmail: (data) => api.post('/api/lead-activities/emails', data),
  addEmailReply: (emailId, replyText) => api.post(`/api/lead-activities/emails/${emailId}/reply`, { reply_text: replyText }),
  deleteEmail: (emailId) => api.delete(`/api/lead-activities/emails/${emailId}`),
  // Visits
  getVisits: (leadId) => api.get(`/api/lead-activities/visits/${leadId}`),
  createVisit: (data) => api.post('/api/lead-activities/visits', data),
  updateVisitStatus: (visitId, status, summary) => api.patch(`/api/lead-activities/visits/${visitId}/status`, { status, summary }),
  deleteVisit: (visitId) => api.delete(`/api/lead-activities/visits/${visitId}`),
  // Calls
  getCalls: (leadId) => api.get(`/api/lead-activities/calls/${leadId}`),
  createCall: (data) => api.post('/api/lead-activities/calls', data),
  deleteCall: (callId) => api.delete(`/api/lead-activities/calls/${callId}`),
  // Meetings
  getMeetings: (leadId) => api.get(`/api/lead-activities/meetings/${leadId}`),
  createMeeting: (data) => api.post('/api/lead-activities/meetings', data),
  updateMeetingStatus: (meetingId, status, summary) => api.patch(`/api/lead-activities/meetings/${meetingId}/status`, { status, summary }),
  rescheduleMeeting: (meetingId, newDate, newTime, notes) => api.patch(`/api/lead-activities/meetings/${meetingId}/reschedule`, { new_date: newDate, new_time: newTime, notes }),
  deleteMeeting: (meetingId) => api.delete(`/api/lead-activities/meetings/${meetingId}`),
  // WhatsApp
  getWhatsAppActivities: (leadId) => api.get(`/api/lead-activities/whatsapp/${leadId}`),
  getWhatsAppConversation: (leadId) => api.get(`/api/whatsapp/conversation/${leadId}`),
  resumeWhatsAppAI: (leadId) => api.post(`/api/whatsapp/conversations/${leadId}/resume-ai`),
  getWhatsAppQuickReplies: (leadId) => api.post(`/api/whatsapp/quick-replies/${leadId}`),
  getLinkedInConversation: (leadId) => api.get(`/api/linkedin/conversation/${leadId}`),
  // Attachments
  getAttachments: (leadId) => api.get(`/api/lead-activities/attachments/${leadId}`),
  uploadAttachment: (leadId, formData) => api.post(`/api/lead-activities/attachments/${leadId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAttachment: (attachmentId) => api.delete(`/api/lead-activities/attachments/${attachmentId}`),
  // Deals (unified with primary deals collection)
  getDeals: (leadId) => api.get(`/api/lead-activities/deals/${leadId}`),
  createDeal: (data) => api.post('/api/lead-activities/deals', data),
  updateDealStatus: (dealId, stage) => api.patch(`/api/lead-activities/deals/${dealId}/status?stage=${stage}`),
  deleteDeal: (dealId) => api.delete(`/api/lead-activities/deals/${dealId}`),
};

// Lead Communication API
export const leadCommunicationAPI = {
  sendEmail: (leadId, data) => api.post(`/api/leads/${leadId}/send-email`, data),
  sendWhatsApp: (leadId, data) => api.post(`/api/leads/${leadId}/send-whatsapp`, data),
  sendSMS: (leadId, data) => api.post(`/api/leads/${leadId}/send-sms`, data),
  getWhatsAppTemplates: () => api.get('/api/social-connectors/whatsapp/templates'),
};

export const emailAttachmentAPI = {
  upload: (formData) => api.post('/api/email-attachments/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Deals API - with Intelligence Refresh
export const dealsAPI = {
  getAll: (params) => api.get('/api/deals/', { params }),
  getOne: (id) => api.get(`/api/deals/${id}`),
  getActivities: (dealId) => api.get(`/api/deals/${dealId}/activities`),
  convertToQuote: (dealId) => api.post(`/api/deals/${dealId}/convert-to-quote`),
  create: (data) => api.post('/api/deals/', data).then(res => {
    emitIntelligenceRefresh('deals', 'create', res.data?.id);
    return res;
  }),
  update: (id, data) => api.put(`/api/deals/${id}`, data).then(res => {
    emitIntelligenceRefresh('deals', 'update', id);
    return res;
  }),
  delete: (id) => api.delete(`/api/deals/${id}`).then(res => {
    emitIntelligenceRefresh('deals', 'delete', id);
    return res;
  }),
  getByLead: (leadId) => api.get(`/api/leads/${leadId}/deals`),
  getSummary: (params) => api.get('/api/deals/summary', { params }),
  listViews: () => api.get('/api/deals/views'),
  createView: (data) => api.post('/api/deals/views', data),
  deleteView: (id) => api.delete(`/api/deals/views/${id}`),
};

// Clients API - with Intelligence Refresh
export const clientsAPI = {
  getAll: (params) => api.get('/api/clients/', { params }),
  getCount: (params) => api.get('/api/clients/count', { params }),
  getOne: (id) => api.get(`/api/clients/${id}`),
  create: (data) => api.post('/api/clients/', data).then(res => {
    emitIntelligenceRefresh('clients', 'create', res.data?.id);
    return res;
  }),
  update: (id, data) => api.put(`/api/clients/${id}`, data).then(res => {
    emitIntelligenceRefresh('clients', 'update', id);
    return res;
  }),
  delete: (id) => api.delete(`/api/clients/${id}`).then(res => {
    emitIntelligenceRefresh('clients', 'delete', id);
    return res;
  }),
  // Address management
  addAddress: (clientId, data) => api.post(`/api/clients/${clientId}/addresses`, data),
  updateAddress: (clientId, addressId, data) => api.put(`/api/clients/${clientId}/addresses/${addressId}`, data),
  deleteAddress: (clientId, addressId) => api.delete(`/api/clients/${clientId}/addresses/${addressId}`),
  // Accounts AI endpoints
  getAIStatus: (clientId) => api.get(`/api/crm/accounts/${clientId}/ai-status`),
  runAccountsAI: (clientId, options = {}) => api.post(`/api/crm/accounts/${clientId}/run-accounts-ai`, options),
  getAIActivities: (clientId) => api.get(`/api/crm/accounts/${clientId}/ai-activities`),
};

// HR API - with Intelligence Refresh
export const hrAPI = {
  getUsers: (params) => api.get('/api/hr/users', { params }),
  getUser: (id) => api.get(`/api/hr/users/${id}`),
  createUser: (data) => api.post('/api/hr/users', data).then(res => {
    emitIntelligenceRefresh('employees', 'create', res.data?.id);
    return res;
  }),
  updateUser: (id, data) => api.put(`/api/hr/users/${id}`, data).then(res => {
    emitIntelligenceRefresh('employees', 'update', id);
    return res;
  }),
  deleteUser: (id) => api.delete(`/api/hr/users/${id}`).then(res => {
    emitIntelligenceRefresh('employees', 'delete', id);
    return res;
  }),
  // Permissions endpoint
  getPermissions: () => api.get('/api/hr/permissions'),
  // Password reset
  resetPassword: (userId, data) => api.post(`/api/hr/users/${userId}/reset-password`, data),
  // Team members endpoint (lighter weight, for dropdowns)
  getTeamMembers: (params) => api.get('/api/hr/team-members', { params }).then(res => ({
    ...res,
    // Normalize to a bare array so every consumer (res.data || [], res.data?.users,
    // normalizeList) keeps working. Uses the permission-free endpoint so NON-admin
    // teammates can also load the assignee list (the old /api/hr/users needed
    // hr.users.view, leaving teammates with an empty "Assigned To" dropdown).
    data: res.data?.members || res.data?.data || (Array.isArray(res.data) ? res.data : []),
  })),
  // HR AI endpoints
  getAIStatus: (userId) => api.get(`/api/crm/hr/${userId}/ai-status`),
  runHRAI: (userId, options = {}) => api.post(`/api/crm/hr/${userId}/run-hr-ai`, options),
  getAIActivities: (userId) => api.get(`/api/crm/hr/${userId}/ai-activities`),
};

// Inventory API - with Intelligence Refresh
export const inventoryAPI = {
  getItems: (params) => api.get('/api/inventory/items', { params }),
  getItem: (id) => api.get(`/api/inventory/items/${id}`),
  createItem: (data) => api.post('/api/inventory/items', data).then(res => {
    emitIntelligenceRefresh('inventory', 'create', res.data?.id);
    return res;
  }),
  updateItem: (id, data) => api.put(`/api/inventory/items/${id}`, data).then(res => {
    emitIntelligenceRefresh('inventory', 'update', id);
    return res;
  }),
  deleteItem: (id) => api.delete(`/api/inventory/items/${id}`).then(res => {
    emitIntelligenceRefresh('inventory', 'delete', id);
    return res;
  }),
  // Item Image functions
  uploadItemImage: (itemId, formData) => api.post(`/api/inventory/items/${itemId}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteItemImage: (itemId, imageIndex) => api.delete(`/api/inventory/items/${itemId}/images/${imageIndex}`),
  // Item Groups
  getItemGroups: (params) => api.get('/api/inventory/item-groups', { params }),
  getItemGroup: (id) => api.get(`/api/inventory/item-groups/${id}`),
  createItemGroup: (data) => api.post('/api/inventory/item-groups', data).then(res => {
    emitIntelligenceRefresh('inventory', 'create', res.data?.id);
    return res;
  }),
  updateItemGroup: (id, data) => api.put(`/api/inventory/item-groups/${id}`, data).then(res => {
    emitIntelligenceRefresh('inventory', 'update', id);
    return res;
  }),
  deleteItemGroup: (id) => api.delete(`/api/inventory/item-groups/${id}`).then(res => {
    emitIntelligenceRefresh('inventory', 'delete', id);
    return res;
  }),
  applyGroupToItems: (groupId) => api.put(`/api/inventory/item-groups/${groupId}/apply-to-items`),
  // Inventory AI endpoints
  getAIStatus: (itemId) => api.get(`/api/crm/inventory/${itemId}/ai-status`),
  runInventoryAI: (itemId, options = {}) => api.post(`/api/crm/inventory/${itemId}/run-inventory-ai`, options),
  getAIActivities: (itemId) => api.get(`/api/crm/inventory/${itemId}/ai-activities`),
};

// AI Activity Feed API (Unified Logs)
export const aiActivityAPI = {
  getLogs: (params) => api.get('/api/crm/ai-activities', { params }),
  getStats: () => api.get('/api/crm/ai/activity-logs/stats'),
};

// Quotes API
export const quotesAPI = {
  getAll: (params) => api.get('/api/quotes/', { params }),
  getOne: (id) => api.get(`/api/quotes/${id}`),
  create: (data) => api.post('/api/quotes/', data),
  update: (id, data) => api.put(`/api/quotes/${id}`, data),
  delete: (id) => api.delete(`/api/quotes/${id}`),
  // Payments
  addPayment: (quoteId, data) => api.post(`/api/quotes/${quoteId}/payments`, data),
  getPayments: (quoteId) => api.get(`/api/quotes/${quoteId}/payments`),
  // Convert to Invoice
  convertToInvoice: (quoteId) => api.post(`/api/quotes/${quoteId}/convert-to-invoice`),
  // E-sign / send for signature
  sendForSignature: (quoteId) => api.post(`/api/quotes/${quoteId}/send-for-signature`),
};

// Invoices API - with Intelligence Refresh
export const invoicesAPI = {
  getAll: (params) => api.get('/api/invoices/', { params }),
  getCount: (params) => api.get('/api/invoices/count', { params }),
  getOne: (id) => api.get(`/api/invoices/${id}`),
  create: (data) => api.post('/api/invoices/', data).then(res => {
    emitIntelligenceRefresh('invoices', 'create', res.data?.id);
    return res;
  }),
  update: (id, data) => api.put(`/api/invoices/${id}`, data).then(res => {
    emitIntelligenceRefresh('invoices', 'update', id);
    return res;
  }),
  delete: (id) => api.delete(`/api/invoices/${id}`).then(res => {
    emitIntelligenceRefresh('invoices', 'delete', id);
    return res;
  }),
  // Payments - emit refresh for accounts dashboard
  addPayment: (invoiceId, data) => api.post(`/api/invoices/${invoiceId}/payments`, data).then(res => {
    emitIntelligenceRefresh('payments', 'create', res.data?.id);
    return res;
  }),
  getPayments: (invoiceId) => api.get(`/api/invoices/${invoiceId}/payments`),
  // Unlinked quotes
  getUnlinkedQuotes: (clientId) => api.get('/api/invoices/quotes/unlinked', { params: { client_id: clientId } }),
  // Get invoices by job_id
  getByJobId: (jobId) => api.get('/api/invoices/', { params: { job_id: jobId } }),
  // Activities
  getActivities: (invoiceId) => api.get(`/api/invoices/${invoiceId}/activities`),
  createActivity: (invoiceId, data) => api.post(`/api/invoices/${invoiceId}/activities`, data),
};

// Taxes API
export const taxesAPI = {
  getAll: (params) => api.get('/api/taxes/', { params }),
  getOne: (id) => api.get(`/api/taxes/${id}`),
  create: (data) => api.post('/api/taxes/', data),
  update: (id, data) => api.put(`/api/taxes/${id}`, data),
  delete: (id) => api.delete(`/api/taxes/${id}`),
};

// Account Statuses API
export const accountStatusesAPI = {
  getQuoteStatuses: () => api.get('/api/account-statuses/quotes'),
  createQuoteStatus: (data) => api.post('/api/account-statuses/quotes', data),
  updateQuoteStatus: (id, data) => api.put(`/api/account-statuses/quotes/${id}`, data),
  deleteQuoteStatus: (id) => api.delete(`/api/account-statuses/quotes/${id}`),
  getInvoiceStatuses: () => api.get('/api/account-statuses/invoices'),
  createInvoiceStatus: (data) => api.post('/api/account-statuses/invoices', data),
  updateInvoiceStatus: (id, data) => api.put(`/api/account-statuses/invoices/${id}`, data),
  deleteInvoiceStatus: (id) => api.delete(`/api/account-statuses/invoices/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/stats'),
  getRecentActivities: () => api.get('/api/dashboard/activities'),
  getActivities: () => api.get('/api/dashboard/activities'),
  
  // AI-Enhanced Sales Dashboard
  getAIEnhancedSales: () => api.get('/api/dashboard/sales/ai-enhanced'),
  scoreLeadAI: (leadId) => api.post(`/api/dashboard/sales/ai-score-lead/${leadId}`),
  getSalesAIRecommendations: () => api.get('/api/dashboard/sales/ai-recommendations'),
};

// Sales AI API (Comprehensive AI Sales Features)
export const salesAIAPI = {
  // Conversation Intelligence
  getConversationIntelligence: (params = {}) => 
    api.get('/api/sales-ai/conversation-intelligence', { params }),
  
  // AI Activity Logs
  getActivityLogs: (params = {}) => 
    api.get('/api/sales-ai/activity-logs', { params }),
  
  // Batch Lead Scoring
  batchScoreLeads: (leadIds = null, persistScores = true) => 
    api.post('/api/sales-ai/batch-score-leads', { 
      lead_ids: leadIds, 
      persist_scores: persistScores 
    }),
  
  // Predictive Analytics
  getPredictiveAnalytics: (days = 30) => 
    api.get('/api/sales-ai/predictive-analytics', { params: { days } }),
  
  // Dashboard Summary
  getDashboardSummary: () => 
    api.get('/api/sales-ai/dashboard-summary'),
};

export const salesGoalsAPI = {
  getAll: () => api.get('/api/sales-goals/'),
  create: (data) => api.post('/api/sales-goals/', data),
  getSummary: () => api.get('/api/sales-goals/summary'),
};

export const campaignsAPI = {
  getAll: (params) => api.get('/api/campaigns/', { params }),
  getOne: (id) => api.get(`/api/campaigns/${id}`),
  getLeads: (id, params) => api.get(`/api/campaigns/${id}/leads`, { params }),
  getRecipients: (id, params) => api.get(`/api/campaigns/${id}/recipients`, { params }),
  exportResults: (id) => api.get(`/api/campaigns/${id}/export`, { responseType: 'blob' }),
  create: (data) => api.post('/api/campaigns/', data),
  activate: (id) => api.post(`/api/campaigns/${id}/activate`),
  pause: (id) => api.post(`/api/campaigns/${id}/pause`),
  retryFailed: (id) => api.post(`/api/campaigns/${id}/retry-failed`),
  delete: (id) => api.delete(`/api/campaigns/${id}`),
  autoCreate: () => api.post('/api/campaigns/auto-create'),
  previewLeads: (data) => api.post('/api/campaigns/preview-leads', data),
  getWhatsappTemplates: () => api.get('/api/campaigns/whatsapp-templates'),
  getEmailTemplates: () => api.get('/api/ai-templates/templates', { params: { category: 'email' } }),
  analyticsOverview: () => api.get('/api/campaigns/analytics/overview'),
  leadJourney: (leadId) => api.get(`/api/campaigns/analytics/journey/${leadId}`),
  templateAnalytics: () => api.get('/api/ai-templates/analytics'),
};

export const brainDigestAPI = {
  getConfig: () => api.get('/api/brain/digest/config'),
  saveConfig: (data) => api.post('/api/brain/digest/config', data),
  preview: (days = 7) => api.get('/api/brain/digest/preview', { params: { days } }),
  send: (days = 7) => api.post('/api/brain/digest/send', { params: { days } }),
  getHistory: (limit = 10) => api.get('/api/brain/digest/history', { params: { limit } }),
};

export const voiceFlowsAPI = {
  getTypes: () => api.get('/api/voice-flows/types'),
  getLeadFlows: (leadId) => api.get(`/api/voice-flows/lead/${leadId}`),
  generate: (leadId, flowType) => api.post(`/api/voice-flows/generate/${leadId}?flow_type=${flowType}`),
};

export const aiTemplatesAPI = {
  getAll: (params) => api.get('/api/ai-templates/templates', { params }),
  install: (id) => api.post(`/api/ai-templates/templates/${id}/install`),
  uninstall: (id) => api.post(`/api/ai-templates/templates/${id}/uninstall`),
  getInstalled: () => api.get('/api/ai-templates/installed'),
  createCustom: (payload) => api.post('/api/ai-templates/custom', payload),
  updateCustom: (id, payload) => api.put(`/api/ai-templates/custom/${id}`, payload),
  sendTest: (payload) => api.post('/api/ai-templates/send-test', payload),
  deleteCustom: (id) => api.delete(`/api/ai-templates/custom/${id}`),
};

export const brainNotificationsAPI = {
  getAll: (params) => api.get('/api/brain/notifications/', { params }),
  markRead: (ids = []) => api.post('/api/brain/notifications/mark-read', ids),
  poll: (lastCheck) => api.get('/api/brain/notifications/poll', { params: { last_check: lastCheck } }),
};




// Accounts AI API (Comprehensive AI Accounts & Collections Features)
export const accountsAIAPI = {
  // Dashboard Summary with AI insights
  getDashboard: () => 
    api.get('/api/accounts-ai/dashboard'),
  
  // Cash Flow Forecasts (7d, 30d, 60d, 90d)
  getCashFlowForecast: () => 
    api.get('/api/accounts-ai/cash-flow-forecast'),
  
  // Collection Risk Analysis
  getCollectionRisk: (minRisk = 0) => 
    api.get('/api/accounts-ai/collection-risk', { params: { min_risk: minRisk } }),
  
  // Payment Predictions
  getPaymentPredictions: () => 
    api.get('/api/accounts-ai/payment-predictions'),
  
  // Client Payment Profiles
  getClientProfiles: () => 
    api.get('/api/accounts-ai/client-profiles'),
  
  // AI Recommendations
  getRecommendations: () => 
    api.get('/api/accounts-ai/recommendations'),
  
  // Send Smart Reminder
  sendReminder: (invoiceId) => 
    api.post(`/api/accounts-ai/send-reminder/${invoiceId}`),
  
  // Batch Score Invoices
  batchScoreInvoices: () => 
    api.post('/api/accounts-ai/batch-score-invoices'),
  
  // Activity Logs
  getActivityLogs: (limit = 50) => 
    api.get('/api/accounts-ai/activity-logs', { params: { limit } }),
};

// Inventory AI API (Comprehensive AI Inventory & Stock Management Features)
export const inventoryAIAPI = {
  // Dashboard Summary with AI insights
  getDashboard: () => 
    api.get('/api/inventory-ai/dashboard'),
  
  // Demand Forecasts (7/30/60/90 days)
  getDemandForecast: () => 
    api.get('/api/inventory-ai/demand-forecast'),
  
  // Stockout Risk Analysis
  getStockoutRisk: (minRisk = 0) => 
    api.get('/api/inventory-ai/stockout-risk', { params: { min_risk: minRisk } }),
  
  // Reorder Recommendations with EOQ
  getReorderRecommendations: () => 
    api.get('/api/inventory-ai/reorder-recommendations'),
  
  // ABC/XYZ Classification
  getABCClassification: () => 
    api.get('/api/inventory-ai/abc-classification'),
  
  // Dead Stock Identification
  getDeadStock: (days = 90) => 
    api.get('/api/inventory-ai/dead-stock', { params: { days } }),
  
  // Supplier Performance Scores
  getSupplierScores: () => 
    api.get('/api/inventory-ai/supplier-scores'),
  
  // Batch Score All Items
  batchScoreItems: () => 
    api.post('/api/inventory-ai/batch-score-items'),
  
  // Generate Reorder PO
  generateReorder: (itemId) => 
    api.post(`/api/inventory-ai/generate-reorder/${itemId}`),
  
  // Activity Logs
  getActivityLogs: (limit = 50) => 
    api.get('/api/inventory-ai/activity-logs', { params: { limit } }),
};

// HR AI API (Comprehensive AI HR Intelligence Features)
export const hrAIAPI = {
  // Dashboard Summary with AI insights
  getDashboard: () => 
    api.get('/api/hr-ai/dashboard'),
  
  // Attrition Risk Analysis
  getAttritionRisk: (minRisk = 0) => 
    api.get('/api/hr-ai/attrition-risk', { params: { min_risk: minRisk } }),
  
  // Performance Analytics
  getPerformanceAnalytics: () => 
    api.get('/api/hr-ai/performance-analytics'),
  
  // Attendance Anomalies Detection
  getAttendanceAnomalies: () => 
    api.get('/api/hr-ai/attendance-anomalies'),
  
  // Promotion Pipeline/Readiness
  getPromotionPipeline: () => 
    api.get('/api/hr-ai/promotion-pipeline'),
  
  // Workforce Planning Forecast
  getWorkforceForecast: () => 
    api.get('/api/hr-ai/workforce-forecast'),
  
  // Batch Score All Employees
  batchScoreEmployees: () => 
    api.post('/api/hr-ai/batch-score-employees'),
  
  // Generate Retention Plan for Employee
  generateRetentionPlan: (employeeId) => 
    api.post(`/api/hr-ai/generate-retention-plan/${employeeId}`),
  
  // Activity Logs
  getActivityLogs: (limit = 50) => 
    api.get('/api/hr-ai/activity-logs', { params: { limit } }),
};

// WebSocket API (Real-time updates)
export const websocketAPI = {
  // Update location via REST (fallback for WebSocket)
  updateLocation: (data) => 
    api.post('/api/ws/location/update', data),
  
  // Get all technician locations
  getTechnicianLocations: () => 
    api.get('/api/ws/location/technicians'),
  
  // Get WebSocket stats
  getStats: () => 
    api.get('/api/ws/stats'),
};

// Settings API
export const settingsAPI = {
  getProfile: () => api.get('/api/settings/profile'),
  updateProfile: (data) => api.put('/api/settings/profile', data),
  updatePassword: (data) => api.put('/api/settings/password', data),
};

// Team API
export const teamAPI = {
  getAll: (params) => api.get('/api/hr/users', { params }),
  getOne: (id) => api.get(`/api/hr/users/${id}`),
  create: (data) => api.post('/api/hr/users', data),
  update: (id, data) => api.put(`/api/hr/users/${id}`, data),
  delete: (id) => api.delete(`/api/hr/users/${id}`),
  invite: (data) => api.post('/api/team/invite', data),
  getOnboardingChecklist: () => api.get('/api/team/onboarding-checklist'),
  completeChecklistStep: (stepId) => api.post(`/api/team/onboarding-checklist/complete/${stepId}`),
  dismissChecklist: () => api.post('/api/team/onboarding-checklist/dismiss'),
  // Team members and attendance endpoints
  getMembers: (params) => api.get('/api/team/members', { params }),
  getAttendance: (params) => api.get('/api/team/attendance', { params }),
};

// Brain API (Autonomous AI Brain)
export const brainAPI = {
  getStats: () => api.get('/api/brain/stats'),
  getDecisions: (limit = 20) => api.get(`/api/brain/decisions?limit=${limit}`),
  getLeadScores: () => api.get('/api/brain/lead-scores'),
  getEmailDrafts: (leadId = null, status = null) => api.get(`/api/brain/email-drafts${leadId ? `?lead_id=${leadId}` : ''}${status ? `${leadId ? '&' : '?'}status=${status}` : ''}`),
  getPendingReviews: () => api.get('/api/brain/pending-reviews'),
  approveEmailDraft: (draftId) => api.post(`/api/brain/email-drafts/${draftId}/approve`),
  dismissEmailDraft: (draftId) => api.post(`/api/brain/email-drafts/${draftId}/dismiss`),
  updateEmailDraft: (draftId, data) => api.put(`/api/brain/email-drafts/${draftId}`, data),
  sendEmailDraft: (draftId) => api.post(`/api/brain/email-drafts/${draftId}/send`),
  bulkSendDrafts: () => api.post('/api/brain/email-drafts/bulk-send'),
  bulkApproveDrafts: () => api.post('/api/brain/email-drafts/bulk-approve'),
  getSmartTiming: (draftId) => api.get(`/api/brain/email-drafts/${draftId}/smart-timing`),
  scheduleSmartSend: (draftId) => api.post(`/api/brain/email-drafts/${draftId}/schedule-smart`),
  bulkScheduleSmart: () => api.post('/api/brain/email-drafts/bulk-schedule-smart'),
  rescheduleEmailDraft: (draftId, scheduledSendAt) => api.post(`/api/brain/email-drafts/${draftId}/reschedule`, { scheduled_send_at: scheduledSendAt }),
  cancelScheduledEmail: (draftId) => api.post(`/api/brain/email-drafts/${draftId}/cancel`),
  getPipelineAnalysis: () => api.get('/api/brain/pipeline-analysis'),
  refreshPipelineAnalysis: () => api.post('/api/brain/pipeline-analysis/refresh'),
  getActivityFeed: (limit = 30) => api.get(`/api/brain/activity-feed?limit=${limit}`),
  getTimeline: (leadId, limit = 50) => api.get(`/api/brain/timeline/${leadId}?limit=${limit}`),
  triggerColdLeadScan: () => api.post('/api/brain/cold-lead-scan'),
  getDigestPreview: (days = 7) => api.get(`/api/brain/digest/preview?days=${days}`),
  sendDigest: (days = 7) => api.post(`/api/brain/digest/send?days=${days}`),
  getDigestConfig: () => api.get('/api/brain/digest/config'),
  saveDigestConfig: (config) => api.post('/api/brain/digest/config', config),
  getDigestHistory: (limit = 10) => api.get(`/api/brain/digest/history?limit=${limit}`),
  getEmailEngagementAnalytics: () => api.get('/api/brain/email/engagement-analytics'),
  getEmailEngagementInsights: () => api.get('/api/brain/email/engagement-insights'),
  getAutoInsightsConfig: () => api.get('/api/brain/email/auto-insights-config'),
  toggleAutoInsights: (enabled) => api.post('/api/brain/email/auto-insights-config', { auto_enabled: enabled }),
  triggerAutoInsights: () => api.post('/api/brain/email/auto-insights-run'),
  getClosingStrategy: (leadId) => api.get(`/api/brain/closing-strategy/${leadId}`),
  generateClosingStrategy: (leadId) => api.post(`/api/brain/closing-strategy/${leadId}`),
  getDealCoaching: (dealId) => api.get(`/api/brain/deal-coaching/${dealId}`),
  generateDealCoaching: (dealId) => api.post(`/api/brain/deal-coaching/${dealId}`),
};

// ICP Learning API
export const icpLearningAPI = {
  getAnalysis: () => api.get('/api/brain/icp-learning/analysis'),
  refineICP: () => api.post('/api/brain/icp-learning/refine'),
  applyRefinement: (learningId) => api.post(`/api/brain/icp-learning/apply/${learningId}`),
};

// Pipeline Health API
export const pipelineHealthAPI = {
  getAnalytics: () => api.get('/api/pipeline-health/analytics'),
  getAICommentary: () => api.get('/api/pipeline-health/ai-commentary'),
};

// Profile API
export const profileAPI = {
  get: () => api.get('/api/profile/'),
  update: (data) => api.put('/api/profile/', data),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/profile/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addAddress: (data) => api.post('/api/profile/addresses', data),
  updateAddress: (id, data) => api.put(`/api/profile/addresses/${id}`, data),
  deleteAddress: (id, type) => api.delete(`/api/profile/addresses/${id}?address_type=${type}`),
  changePassword: (data) => api.put('/api/profile/change-password', data),
  getAIPreferences: () => api.get('/api/profile/ai-preferences'),
  updateAIPreferences: (data) => api.put('/api/profile/ai-preferences', data),
  // Privacy & Data
  getPrivacyStatus: () => api.get('/api/profile/privacy-status'),
  acceptPrivacy: () => api.post('/api/profile/accept-privacy'),
  requestDeletion: (data) => api.post('/api/profile/request-deletion', data),
  cancelDeletion: () => api.post('/api/profile/cancel-deletion'),
  exportDataJson: () => api.get('/api/profile/export-data', { responseType: 'blob' }),
  exportDataCsv: () => api.get('/api/profile/export-data-csv', { responseType: 'blob' }),
  updateMarketingConsent: (data) => api.put('/api/profile/marketing-consent', data),
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/api/payments/', { params }),
  getOne: (id) => api.get(`/api/payments/${id}`),
  create: (data) => api.post('/api/payments/', data),
  update: (id, data) => api.put(`/api/payments/${id}`, data),
  delete: (id) => api.delete(`/api/payments/${id}`),
};

// AI Schedule API (Phase F - Autonomous AI Scheduling)
export const aiScheduleAPI = {
  // List all schedules for tenant
  getAll: (params) => api.get('/api/ai/schedules', { params }),
  // Get schedule for specific agent type
  getByAgent: (agentType) => api.get(`/api/ai/schedules/agent/${agentType}`),
  // Get schedule by ID
  getOne: (id) => api.get(`/api/ai/schedules/${id}`),
  // Create new schedule (one per agent per tenant)
  create: (data) => api.post('/api/ai/schedules', data),
  // Update schedule
  update: (id, data) => api.put(`/api/ai/schedules/${id}`, data),
  // Delete schedule
  delete: (id) => api.delete(`/api/ai/schedules/${id}`),
  // Toggle schedule enabled/disabled
  toggle: (id) => api.post(`/api/ai/schedules/${id}/toggle`),
  // Manual trigger for testing
  trigger: (id) => api.post(`/api/ai/schedules/${id}/trigger`),
  // Get run logs for schedule
  getLogs: (id, limit = 50) => api.get(`/api/ai/schedules/${id}/logs`, { params: { limit } }),
  // Get all logs for tenant
  getAllLogs: (params) => api.get('/api/ai/schedules/logs/all', { params }),
  // Get default config for agent type
  getDefaults: (agentType) => api.get(`/api/ai/schedules/defaults/${agentType}`),
};

// Notification API (Phase E - Customer Portal & Notifications)
export const notificationAPI = {
  // Get notification events for tenant
  getEvents: (params) => api.get('/api/notifications/events', { params }),
  // Get specific notification event
  getEvent: (id) => api.get(`/api/notifications/events/${id}`),
  // Get notification summary
  getSummary: () => api.get('/api/notifications/summary'),
  // Get notification preferences
  getPreferences: () => api.get('/api/notifications/preferences'),
  // Update notification preferences
  updatePreferences: (data) => api.post('/api/notifications/preferences', data),
  // Generate test notification
  generateTest: (type) => api.post(`/api/notifications/test/generate?notification_type=${type}`),
};

// Billing Portal API (Phase E - Customer Portal)
export const billingPortalAPI = {
  openPortal: () => api.post('/api/tenant/billing/customer-portal'),
};

// Full Billing API (replaces raw fetch calls in Billing.js)
export const billingAPI = {
  // Plans
  getPlans: () => api.get('/api/tenant/billing/plans'),
  getCurrentPlan: () => api.get('/api/tenant/billing/plan'),
  changePlan: (data) => api.post('/api/tenant/billing/plan/change', data),
  // Usage & Status
  getUsage: () => api.get('/api/tenant/billing/usage'),
  getLiveEligibility: () => api.get('/api/tenant/billing/live-eligibility'),
  getInvoicePreview: () => api.get('/api/tenant/billing/invoice/preview'),
  getRenewalPaymentStatus: () => api.get('/api/tenant/billing/renewal-payment-status'),
  // Stripe
  getStripeMode: () => api.get('/api/subscriptions/stripe/mode'),
  createCheckout: (data) => api.post('/api/tenant/billing/checkout', data),
  createCustomerPortal: () => api.post('/api/tenant/billing/customer-portal'),
  syncAfterCheckout: (data) => api.post('/api/tenant/billing/sync-after-checkout', data),
  syncStripe: () => api.post('/api/tenant/billing/sync-stripe'),
  // Credits
  getCredits: () => api.get('/api/tenant/billing/credits'),
  getCreditHistory: () => api.get('/api/tenant/billing/credits/history'),
  getCreditTransactions: (limit = 20, skip = 0) => api.get(`/api/tenant/billing/credits/transactions?limit=${limit}&skip=${skip}`),
  getUsageHistory: (period = 'week', category = 'all', fromDate = '', toDate = '') => api.get(`/api/tenant/billing/credits/usage-history`, { params: { period, category, from_date: fromDate || undefined, to_date: toDate || undefined } }),
  exportUsageHistory: (period = 'month', category = 'all', fromDate = '', toDate = '') => api.get(`/api/tenant/billing/credits/usage-export`, { params: { period, category, from_date: fromDate || undefined, to_date: toDate || undefined }, responseType: 'blob' }),
  getSpendAttribution: (period = 'month', category = 'all') => api.get(`/api/tenant/billing/credits/attribution`, { params: { period, category } }),
  getLowBalanceAlert: () => api.get('/api/tenant/billing/credits/low-balance-alert'),
  setLowBalanceAlert: (enabled, threshold_credits) => api.put('/api/tenant/billing/credits/low-balance-alert', { enabled, threshold_credits }),
  getCreditSummary: () => api.get('/api/tenant/billing/credits/summary'),
  getCostDashboard: () => api.get('/api/tenant/billing/credits/cost-dashboard'),
  getLlmCostDashboard: (days = 30) => api.get(`/api/tenant/billing/llm-cost-dashboard?days=${days}`),
  purchaseCredits: (data) => api.post('/api/tenant/billing/credits/purchase', data),
  purchaseCustomCredits: (data) => api.post('/api/tenant/billing/credits/purchase-custom', data),
  syncCreditPurchase: (sessionId) => api.post(`/api/tenant/billing/credits/sync?session_id=${sessionId}`),
  syncCreditPool: (sessionId) => api.post(`/api/tenant/billing/credit-pool/sync?session_id=${sessionId}`),
  // Auto Top-up
  getAutoTopup: () => api.get('/api/tenant/billing/credits/auto-topup'),
  setAutoTopup: (data) => api.put('/api/tenant/billing/credits/auto-topup', data),
  getActionPricing: () => api.get('/api/tenant/billing/credits/pricing'),
  getCalculatorData: () => api.get('/api/tenant/billing/credits/calculator-data'),
  estimateCredits: (volumes) => api.post('/api/tenant/billing/credits/estimate', { volumes }),
  // Invoices & Payments
  getInvoices: () => api.get('/api/billing/invoices'),
  getPayments: () => api.get('/api/billing/payments'),
  getSubscriptionInvoices: () => api.get('/api/subscriptions/invoices'),
};

// Razorpay (India) API — gateway routing + INR subscriptions + credit orders
export const razorpayAPI = {
  getGatewayConfig: (country) => api.get(`/api/tenant/billing/gateway-config${country ? `?country=${country}` : ''}`),
  createSubscription: (data) => api.post('/api/tenant/billing/razorpay/checkout', data),
  upgradeSubscription: (data) => api.post('/api/tenant/billing/razorpay/upgrade', data),
  syncSubscription: (data) => api.post('/api/tenant/billing/razorpay/sync', data || {}),
  createCreditOrder: (data) => api.post('/api/tenant/billing/razorpay/credits/order', data),
  createCustomCreditOrder: (data) => api.post('/api/tenant/billing/razorpay/credits/order-custom', data),
  verifyCreditPayment: (data) => api.post('/api/tenant/billing/razorpay/credits/verify', data),
};

// HR Settings API (Departments & Roles)
export const hrSettingsAPI = {
  getDepartments: () => api.get('/api/hr/departments'),
  createDepartment: (data) => api.post('/api/hr/departments', data),
  updateDepartment: (id, data) => api.put(`/api/hr/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/api/hr/departments/${id}`),
  getPositions: () => api.get('/api/hr/roles'),
  createPosition: (data) => api.post('/api/hr/roles', data),
  updatePosition: (id, data) => api.put(`/api/hr/roles/${id}`, data),
  deletePosition: (id) => api.delete(`/api/hr/roles/${id}`),
};

// Schedule API
export const scheduleAPI = {
  getEvents: (params) => api.get('/api/schedule/events', { params }),
  getCalendarEvents: (params) => api.get('/api/schedule/calendar-events', { params }),
  createEvent: (data) => api.post('/api/schedule/events', data),
  updateEvent: (id, data) => api.put(`/api/schedule/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/api/schedule/events/${id}`),
  getTasks: (params) => api.get('/api/schedule/tasks', { params }),
  getFollowups: (params) => api.get('/api/schedule/followups', { params }),
  getVisits: (params) => api.get('/api/schedule/visits', { params }),
  getMeetings: (params) => api.get('/api/schedule/meetings', { params }),
};

// Reports API
export const reportsAPI = {
  getSalesReport: (params) => api.get('/api/reports/sales', { params }),
  getLeoImpact: (params) => api.get('/api/reports/leo-impact', { params }),
  getLeadsReport: (params) => api.get('/api/reports/leads', { params }),
  getPipelineReport: () => api.get('/api/reports/leads'),
  getRevenueReport: (params) => api.get('/api/reports/revenue', { params }),
  getHRReport: (params) => api.get('/api/reports/hr', { params }),
  getWinLossReport: (params) => api.get('/api/reports/win-loss', { params }),
  getPipelineVelocity: (params) => api.get('/api/reports/pipeline-velocity', { params }),
  getConversionFunnel: (params) => api.get('/api/reports/conversion-funnel', { params }),
};

// Deal Approvals API
export const dealApprovalsAPI = {
  getRules: () => api.get('/api/deal-approvals/rules'),
  createRule: (data) => api.post('/api/deal-approvals/rules', data),
  updateRule: (id, data) => api.put(`/api/deal-approvals/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/api/deal-approvals/rules/${id}`),
  submitDeal: (dealId) => api.post(`/api/deal-approvals/deals/${dealId}/submit`),
  getPending: () => api.get('/api/deal-approvals/pending'),
  getHistory: (params) => api.get('/api/deal-approvals/history', { params }),
  takeAction: (approvalId, data) => api.post(`/api/deal-approvals/${approvalId}/action`, data),
};

// CPQ Engine API
export const cpqAPI = {
  getRules: () => api.get('/api/cpq/rules'),
  createRule: (data) => api.post('/api/cpq/rules', data),
  updateRule: (id, data) => api.put(`/api/cpq/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/api/cpq/rules/${id}`),
  calculatePrices: (data) => api.post('/api/cpq/calculate', data),
};

// Currency API
export const currencyAPI = {
  getRates: () => api.get('/api/currency/rates'),
  updateRates: (rates) => api.put('/api/currency/rates', { rates }),
  convert: (amount, from, to) => api.get(`/api/currency/convert?amount=${amount}&from_currency=${from}&to_currency=${to}`),
};

// CRM Features API
export const commissionAPI = {
  getRules: () => api.get('/api/crm/commission/rules'),
  createRule: (data) => api.post('/api/crm/commission/rules', data),
  deleteRule: (id) => api.delete(`/api/crm/commission/rules/${id}`),
  getDashboard: () => api.get('/api/crm/commission/dashboard'),
};

export const forecastingAPI = {
  getForecast: (period) => api.get(`/api/crm/forecasting?period=${period || 'quarter'}`),
};

export const emailAnalyticsAPI = {
  getAnalytics: () => api.get('/api/crm/email-analytics'),
};

export const collaborationAPI = {
  getComments: (dealId) => api.get(`/api/crm/deals/${dealId}/comments`),
  addComment: (dealId, data) => api.post(`/api/crm/deals/${dealId}/comments`, data),
  deleteComment: (dealId, commentId) => api.delete(`/api/crm/deals/${dealId}/comments/${commentId}`),
  getNotifications: () => api.get('/api/crm/notifications'),
  markRead: (id) => api.put(`/api/crm/notifications/${id}/read`),
  watchDeal: (dealId) => api.post(`/api/crm/deals/${dealId}/watch`),
  unwatchDeal: (dealId) => api.delete(`/api/crm/deals/${dealId}/watch`),
};

export const dealCoachingAPI = {
  getCoaching: (dealId) => api.post(`/api/crm/deals/${dealId}/ai-coach`),
  getHistory: (dealId) => api.get(`/api/crm/deals/${dealId}/coaching-history`),
};


// Marketplace API
export const marketplaceAPI = {
  getProducts: (params) => api.get('/api/marketplace/products', { params }),
  getProduct: (id) => api.get(`/api/marketplace/products/${id}`),
  getCategories: () => api.get('/api/marketplace/categories'),
  getOrders: (params) => api.get('/api/marketplace/orders', { params }),
};

// Ecommerce API
export const ecommerceAPI = {
  getProducts: (params) => api.get('/api/ecommerce/products', { params }),
  getProduct: (id) => api.get(`/api/ecommerce/products/${id}`),
  createProduct: (data) => api.post('/api/ecommerce/products', data),
  updateProduct: (id, data) => api.put(`/api/ecommerce/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/api/ecommerce/products/${id}`),
  getOrders: (params) => api.get('/api/ecommerce/orders', { params }),
  getOrder: (id) => api.get(`/api/ecommerce/orders/${id}`),
  updateOrderStatus: (id, data) => api.put(`/api/ecommerce/orders/${id}/status`, data),
};

// API Keys API
export const apiKeysAPI = {
  getAll: () => api.get('/api/api-keys/'),
  create: (data) => api.post('/api/api-keys/', data),
  delete: (id) => api.delete(`/api/api-keys/${id}`),
  regenerate: () => api.post(`/api/api-keys/regenerate`),
};


// Org Visibility API (Phase G Step 7)
export const orgVisibilityAPI = {
  getTenants: (params) => api.get('/api/org/tenants', { params }),
  getTenantAIStatus: (tenantId) => api.get(`/api/org/tenants/${tenantId}/ai-status`),
  getTenantBudget: (tenantId) => api.get(`/api/org/tenants/${tenantId}/budget`),
  getOrgOverview: () => api.get('/api/org/overview'),
};

// Enterprise Roles API (Phase G)
export const enterpriseRolesAPI = {
  getPermissions: () => api.get('/api/enterprise/permissions'),
  getUserRole: () => api.get('/api/enterprise/my-role'),
};

// Voice AI API (Phase K + Phase N)
export const voiceAPI = {
  // Get Voice AI status for current tenant
  getStatus: () => api.get('/api/voice/status'),
  
  // Initiate outbound call
  initiateOutboundCall: (data) => api.post('/api/voice/calls/outbound', data),
  
  // Get call history for an entity
  getCallHistory: (entityType, entityId, limit = 20) => 
    api.get(`/api/voice/calls?entity_type=${entityType}&entity_id=${entityId}&limit=${limit}`),
  
  // Get call details
  getCallDetails: (callId) => api.get(`/api/voice/calls/${callId}`),
  
  // Get call summary
  getCallSummary: (callId) => api.get(`/api/voice/summaries/${callId}`),
  
  // Get KB usage for a call (Phase L)
  getKBUsage: (callId) => api.get(`/api/voice/calls/${callId}/kb-usage`),
  
  // Phase N: Human Transfer APIs
  // Transfer call to human agent
  transferToHuman: (callId, triggerReason = 'manual_transfer', additionalContext = null) => 
    api.post('/api/voice/transfer', {
      call_id: callId,
      trigger_reason: triggerReason,
      additional_context: additionalContext
    }),
  
  // Get handoff details for a call
  getHandoffDetails: (callId) => api.get(`/api/voice/calls/${callId}/handoff`),

  // Dedicated Twilio number provisioning (platform-managed caller IDs)
  searchNumbers: (country = 'US', areaCode = '', limit = 10) =>
    api.get(`/api/voice/numbers/search?country=${country}${areaCode ? `&area_code=${areaCode}` : ''}&limit=${limit}`),
  listNumbers: () => api.get('/api/voice/numbers'),
  purchaseNumber: (phoneNumber) => api.post('/api/voice/numbers/purchase', { phone_number: phoneNumber }),
  releaseNumber: (twilioSid) => api.delete(`/api/voice/numbers/${twilioSid}`),
  
  // Get tenant transfer settings
  getTransferSettings: () => api.get('/api/voice/transfer/settings'),

  // Phase 3D: safe agent provisioning verification (no call placed)
  testProvisionAgent: (data) => api.post('/api/voice/agent/test-provision', data),
  // Phase 3C: per-tenant native RAG (graduation)
  getNativeRagSettings: () => api.get('/api/voice/native-rag/settings'),
  updateNativeRagSettings: (enabled) => api.put('/api/voice/native-rag/settings', { enabled }),
  syncNativeRag: () => api.post('/api/voice/native-rag/sync'),
  
  // Update tenant transfer settings (admin only)
  updateTransferSettings: (humanTransferEnabled, fallbackAgentNumber) =>
    api.put('/api/voice/transfer/settings', {
      human_transfer_enabled: humanTransferEnabled,
      fallback_agent_number: fallbackAgentNumber
    }),
  
  // Check if call can be transferred
  canTransfer: (callId) => api.get(`/api/voice/transfer/can-transfer/${callId}`),

  // Brain-initiated calls (Phase C)
  initiateBrainCall: (leadId, callType = null) =>
    api.post(`/api/voice/brain-call/${leadId}`, null, { params: callType ? { call_type: callType } : {} }),
  getBrainCallStatus: (callId) => api.get(`/api/voice/brain-call/${callId}/status`),
  hangupBrainCall: (callId) => api.post(`/api/voice/brain-call/${callId}/hangup`),
  getBrainCallsForLead: (leadId, limit = 20) => api.get(`/api/voice/brain-calls/lead/${leadId}?limit=${limit}`),
  getBrainCallsDashboard: (params = {}) => api.get('/api/voice/brain-calls/dashboard', { params }),
  // Call scheduling (Phase C+)
  scheduleCall: (leadId, data) => api.post(`/api/voice/call-queue/schedule/${leadId}`, data),
  getCallQueue: (params = {}) => api.get('/api/voice/call-queue', { params }),
  getCallQueueForLead: (leadId) => api.get(`/api/voice/call-queue/lead/${leadId}`),
  cancelScheduledCall: (queueId) => api.post(`/api/voice/call-queue/${queueId}/cancel`),
  rescheduleCall: (queueId, data) => api.post(`/api/voice/call-queue/${queueId}/reschedule`, data),
  getOptimalTime: (leadId) => api.get(`/api/voice/call-queue/optimal-time/${leadId}`),

  // Hybrid Voice Persona
  getPersona: () => api.get('/api/voice/persona'),
  getPersonaOverrides: () => api.get('/api/voice/persona/overrides'),
  updatePersonaOverrides: (data) => api.put('/api/voice/persona/overrides', data),
  previewPersonaPrompt: () => api.get('/api/voice/persona/preview'),

  // Inbound Voice Hub
  getInboundHealth: () => api.get('/api/voice/inbound/health'),
  provisionExotelInbound: () => api.post('/api/tenant/integrations/exotel/provision-inbound'),
  governorInboundCollisions: () => api.get('/api/voice/inbound/governor/collisions'),
  governorFixExotelMapping: (data) => api.post('/api/voice/inbound/governor/fix-exotel-mapping', data),
  // VoiceLink
  connectVoicelink: (username, password) => api.post('/api/voice/inbound/voicelink/connect', { username, password }),
  enableVoicelinkOutbound: () => api.post('/api/voice/inbound/voicelink/enable-outbound'),
  getVoicelinkAudit: () => api.get('/api/voice/inbound/voicelink/audit'),
  // Reseller-managed VoiceLink number flow (KYC-gated buy)
  getVoicelinkKyc: () => api.get('/api/voice/voicelink/kyc'),
  submitVoicelinkKycStep: (step, payload) => api.post('/api/voice/voicelink/kyc/step', { step, payload }),
  uploadVoicelinkKycDocument: (documentType, file) => {
    const fd = new FormData();
    fd.append('document_type', documentType);
    fd.append('file', file);
    return api.post('/api/voice/voicelink/kyc/upload-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getVoicelinkAvailable: (didType = 2) => api.get('/api/voice/voicelink/available', { params: { did_type: didType } }),
  buyVoicelinkNumber: (didId) => api.post('/api/voice/voicelink/buy', { did_id: didId }),
  adoptVoicelinkNumber: (payload) => api.post('/api/voice/voicelink/adopt', payload),
  enableVoicelinkOutboundManaged: () => api.post('/api/voice/voicelink/enable-outbound'),
  reconnectVoicelink: () => api.post('/api/voice/voicelink/reconnect'),
  reclaimVoicelinkNumber: () => api.post('/api/voice/voicelink/reclaim'),
  voicelinkConnectivity: () => api.get('/api/voice/voicelink/connectivity'),
  releaseVoicelinkNumber: () => api.post('/api/voice/voicelink/release'),
  placeVoiceTestCall: (payload) => api.post('/api/voice/inbound/test-call', payload),
  routingPreview: (toNumber) => api.get('/api/voice/inbound/routing-preview', { params: { to_number: toNumber } }),
  governorOrphanUsers: () => api.get('/api/voice/inbound/governor/orphan-users'),
};

// Meeting Scheduler API
export const meetingSchedulerAPI = {
  getLeadMeetings: (leadId) => api.get(`/api/meetings/lead/${leadId}`),
  getUpcomingMeetings: (limit = 20) => api.get(`/api/meetings/upcoming?limit=${limit}`),
  getLeadBookingLink: (leadId) => api.get(`/api/meetings/booking-link/lead/${leadId}`),
};

// Knowledge Base API (Phase L + Phase M Multilingual)
export const knowledgeAPI = {
  // List all sources with optional language filter (Phase M)
  getSources: (sourceType, status, languageCode) => {
    const params = new URLSearchParams();
    if (sourceType) params.append('source_type', sourceType);
    if (status) params.append('status_filter', status);
    if (languageCode) params.append('language_code', languageCode);
    return api.get(`/api/knowledge/sources?${params.toString()}`);
  },
  
  // Upload new source with language support (Phase M)
  uploadSource: (file, name, sourceType, languageCode = 'en', isTranslation = false, sourceParentId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('source_type', sourceType);
    formData.append('language_code', languageCode);
    formData.append('is_translation', isTranslation);
    if (sourceParentId) {
      formData.append('source_parent_id', sourceParentId);
    }
    return api.post('/api/knowledge/sources', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Get source details
  getSource: (sourceId) => api.get(`/api/knowledge/sources/${sourceId}`),
  
  // Update source status
  updateSourceStatus: (sourceId, status) => 
    api.patch(`/api/knowledge/sources/${sourceId}/status`, { status }),
  
  // Delete source
  deleteSource: (sourceId) => api.delete(`/api/knowledge/sources/${sourceId}`),
  
  // Get source chunks
  getSourceChunks: (sourceId) => api.get(`/api/knowledge/sources/${sourceId}/chunks`),

  // Iter 60 — Re-chunk + re-embed an existing source from its stored content
  reprocessSource: (sourceId) => api.post(`/api/knowledge/sources/${sourceId}/reprocess`),
  
  // Search knowledge base with language filter (Phase M)
  search: (query, topK = 5, sourceTypes = null, languageCode = null) => 
    api.post('/api/knowledge/search', { 
      query, 
      top_k: topK, 
      source_types: sourceTypes,
      language_code: languageCode 
    }),

  // "Wow" conversational test — answers like the live AI would (grounded RAG, billed)
  testAnswer: (query, languageCode = 'en', history = null) =>
    api.post('/api/knowledge/test-answer', { query, language_code: languageCode, history }, { timeout: 60000 }),

  // KB Gap Autopilot — save a Q&A (e.g. a general-expertise answer) into the KB
  saveAnswer: (question, answer, title = null) =>
    api.post('/api/knowledge/save-answer', { question, answer, title }, { timeout: 60000 }),
  
  // Get KB stats
  getStats: () => api.get('/api/knowledge/stats'),

  // Phase 4 — KB health (counts, embedding model, last scrape, per-source coverage)
  getHealth: () => api.get('/api/knowledge/health'),

  // Phase 4 — re-scrape the tenant website and rebuild its KB (long-running)
  refreshWebsite: (websiteUrl = null) =>
    api.post('/api/knowledge/refresh-website', { website_url: websiteUrl }, { timeout: 120000 }),

  // Additively scrape ANY URL/sitemap into the KB (does not wipe existing sources)
  scrapeUrl: (url) =>
    api.post('/api/knowledge/scrape-url', { url }, { timeout: 120000 }),

  // KB coverage indicator (buyer-critical topics)
  getCoverage: () => api.get('/api/knowledge/coverage'),

  // AI-draft a missing coverage topic (review before saving)
  draftCoverageTopic: (topic) =>
    api.post('/api/knowledge/coverage/draft', { topic }, { timeout: 60000 }),

  // Save a reviewed coverage-topic article into the KB
  saveCoverageTopic: (topic, title, content) =>
    api.post('/api/knowledge/coverage/save', { topic, title, content }),

  // Auto re-scrape schedule
  getRescrapeSchedule: () => api.get('/api/knowledge/rescrape-schedule'),
  setRescrapeSchedule: (frequency) => api.put('/api/knowledge/rescrape-schedule', { frequency }),
};

// Stripe Connect — tenant collects payments from their clients
export const stripeConnectAPI = {
  getStatus: (live = false) => api.get(`/api/tenant/stripe/status${live ? '?live=true' : ''}`),
  connect: () => api.post('/api/tenant/stripe/connect'),
};

// Voice Analytics API (Phase O)
export const analyticsAPI = {
  // Get overview analytics
  getOverview: (days = 30) => api.get(`/api/voice/analytics/overview?days=${days}`),
  
  // Get objection analytics
  getObjections: (days = 30) => api.get(`/api/voice/analytics/objections?days=${days}`),
  
  // Get coaching insights
  getInsights: (severity = null) => {
    const params = severity ? `?severity=${severity}` : '';
    return api.get(`/api/voice/analytics/insights${params}`);
  },
  
  // Generate analytics
  generate: (days = 30, includeInsights = true) => 
    api.post('/api/voice/analytics/generate', { days, include_insights: includeInsights }),
  
  // Get summary for widgets
  getSummary: () => api.get('/api/voice/analytics/summary'),
};

// Exports API (Phase R)
export const exportsAPI = {
  // Get available export types
  getTypes: () => api.get('/api/exports/types'),
  
  // Request new export
  request: (exportType, dateFrom, dateTo) => 
    api.post('/api/exports/request', {
      export_type: exportType,
      date_from: dateFrom,
      date_to: dateTo
    }),
  
  // Get export status
  getStatus: (exportId) => api.get(`/api/exports/status/${exportId}`),
  
  // Download export file
  download: (exportId) => api.get(`/api/exports/download/${exportId}`, { responseType: 'blob' }),
  
  // Get download URL
  getDownloadUrl: (exportId) => `${process.env.REACT_APP_BACKEND_URL}/api/exports/download/${exportId}`,
  
  // Get export history
  getHistory: (limit = 50) => api.get(`/api/exports/history?limit=${limit}`),
  
  // Delete export
  delete: (exportId) => api.delete(`/api/exports/${exportId}`),
  
  // BI Connector - paginated data
  getBIData: (exportType, dateFrom, dateTo, page = 1, pageSize = 100) => 
    api.get(`/api/exports/bi/${exportType}`, {
      params: {
        date_from: dateFrom,
        date_to: dateTo,
        page,
        page_size: pageSize
      }
    }),
  
  // Get audit log
  getAuditLog: (limit = 100) => api.get(`/api/exports/audit-log?limit=${limit}`),
  
  // Trigger cleanup
  cleanup: () => api.post('/api/exports/cleanup'),
};

// Compliance API (Phase P)
export const complianceAPI = {
  // Get compliance settings
  getSettings: () => api.get('/api/compliance/settings'),
  
  // Update compliance settings
  updateSettings: (settings) => api.put('/api/compliance/settings', settings),
  
  // Evaluate compliance for a call
  evaluate: (callerNumber) => api.get(`/api/compliance/evaluate?caller_number=${encodeURIComponent(callerNumber)}`),
  
  // Get audit log
  getAuditLog: (limit = 100) => api.get(`/api/compliance/audit-log?limit=${limit}`),
};



// PLG API (Product-Led Growth - Activation, Analytics)
export const plgAPI = {
  // Activation Tracking
  trackEvent: (eventType, eventData = null, source = 'app') => 
    api.post('/api/plg/track-event', { event_type: eventType, event_data: eventData, source }),
  getActivationStatus: () => api.get('/api/plg/activation-status'),
  
  // Plan Limits & AI Access
  getPlanLimits: () => api.get('/api/plg/plan-limits'),
  checkAIAccess: (agentType) => api.get(`/api/plg/check-ai-access?agent_type=${agentType}`),
  
  // Governance Analytics (Superadmin)
  getSignupAnalytics: (period = '30d') => api.get(`/api/plg/governance/signup-analytics?period=${period}`),
  getActivationFunnel: (period = '30d') => api.get(`/api/plg/governance/activation-funnel?period=${period}`),
  getRecentSignups: (limit = 20) => api.get(`/api/plg/governance/recent-signups?limit=${limit}`),
};

// Breach Notification API
export const breachAPI = {
  list: () => api.get('/api/compliance/breach'),
  get: (id) => api.get(`/api/compliance/breach/${id}`),
  report: (data) => api.post('/api/compliance/breach', data),
  update: (id, data) => api.put(`/api/compliance/breach/${id}`, data),
  markNotified: (id) => api.post(`/api/compliance/breach/${id}/notify`),
};

// AI Monitoring API
export const aiMonitoringAPI = {
  getCostUsage: () => api.get('/api/ai/cost-usage'),
  getCostTransactions: () => api.get('/api/ai/cost-transactions'),
  getBudgetState: () => api.get('/api/ai/budget-state'),
  getConfidenceDashboard: () => api.get('/api/ai/confidence/dashboard/enhanced'),
  getAutoPaused: () => api.get('/api/ai/confidence/auto-paused'),
};


// Cadences API (AI-powered follow-up sequences)
export const cadencesAPI = {
  list: (params) => api.get('/api/brain/cadences', { params }),
  generate: (leadId) => api.post(`/api/brain/cadences/generate/${leadId}`),
  getAnalytics: () => api.get('/api/brain/cadence-analytics/analytics'),
  getChannelOverview: () => api.get('/api/brain/cadences/channel-overview'),
};

// ICP API (Ideal Customer Profile)
export const icpAPI = {
  get: () => api.get('/api/icp/'),
  getBrainICP: () => api.get('/api/brain/icp'),
  generate: (data) => api.post('/api/brain/icp/generate', data),
  discover: (params) => api.post('/api/brain/icp/discover', params),
};


// Workflows API (AI Workflow Builder)
export const workflowsAPI = {
  list: (params) => api.get('/api/brain/workflows/', { params }),
  get: (id) => api.get(`/api/brain/workflows/${id}`),
  create: (data) => api.post('/api/brain/workflows/', data),
  update: (id, data) => api.put(`/api/brain/workflows/${id}`, data),
  delete: (id) => api.delete(`/api/brain/workflows/${id}`),
  activate: (id) => api.post(`/api/brain/workflows/${id}/activate`),
  pause: (id) => api.post(`/api/brain/workflows/${id}/pause`),
  executions: (id) => api.get(`/api/brain/workflows/${id}/executions`),
  suggest: () => api.post('/api/brain/workflows/suggest', {}, { timeout: 15000 }),
  suggestStatus: (taskId) => api.get(`/api/brain/workflows/suggest/status/${taskId}`),
  acceptSuggestion: (data) => api.post('/api/brain/workflows/suggest/accept', data),
};


export const documentLibraryAPI = {
  list: (params) => api.get('/api/document-library/', { params }),
  get: (id) => api.get(`/api/document-library/${id}`),
  upload: (formData) => api.post('/api/document-library/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  update: (id, data) => api.put(`/api/document-library/${id}`, data),
  delete: (id) => api.delete(`/api/document-library/${id}`),
  download: (id) => api.get(`/api/document-library/${id}/download`, { responseType: 'blob' }),
  categories: () => api.get('/api/document-library/meta/categories'),
};


export const docRequirementsAPI = {
  templates: (params) => api.get('/api/doc-requirements/templates', { params }),
  getIndustryTemplate: (slug) => api.get(`/api/doc-requirements/templates/${slug}`),
  createTemplate: (data) => api.post('/api/doc-requirements/templates', data),
  applyToLead: (data) => api.post('/api/doc-requirements/lead-requirements', data),
  getLeadRequirements: (leadId, direction) => api.get(`/api/doc-requirements/lead-requirements/${leadId}`, { params: { direction } }),
  markReceived: (leadId, data) => api.post(`/api/doc-requirements/lead-requirements/${leadId}/mark-received`, data),
};

export const emailDraftsAPI = {
  updateAttachments: (draftId, docIds) => api.post(`/api/brain/email-drafts/${draftId}/attachments`, { doc_ids: docIds }),
};

export const brainDocProcessorAPI = {
  registerInboundEmail: (data) => api.post('/api/brain/doc-processor/inbound-email', data, { timeout: 120000 }),
  classifyDocument: (data) => api.post('/api/brain/doc-processor/classify-document', data, { timeout: 120000 }),
  getVerificationSummary: (leadId) => api.get(`/api/brain/doc-processor/doc-verification/${leadId}`),
  listInboundEmails: (params) => api.get('/api/brain/doc-processor/inbound-emails', { params }),
  triggerFollowup: (leadId) => api.post(`/api/brain/doc-processor/trigger-followup/${leadId}`, {}, { timeout: 90000 }),
};
